import CloseIcon from "@mui/icons-material/Close";
import { Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Stack, TextField, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { LoadingComp } from "../../../../shared/components/loading/Loading";
import { useToast } from "../../../../shared/hooks";
import { IAgreementInstallment, SinisterAgreementService } from "../../../../shared/services/api/sinister/SinisterAgreementService";
import { formatDateFnsRemoveTimezone } from "../../../../shared/utils/format-date";
import { formatScaleValueBR } from "../../../../shared/utils/formatScaleValue";

interface Props {
	open: boolean;
	agreementId: string;
	installment: IAgreementInstallment;
	onClose: () => void;
	onConfirm: () => void;
}

export const ModalCancelInstallment = ({ open, agreementId, installment, onClose, onConfirm }: Props) => {
	const toastHandler = useToast();
	const [reason, setReason] = useState("");
	const [reasonError, setReasonError] = useState(false);
	const [isSubmitting, setIsSubmitting] = useState(false);

	useEffect(() => {
		if (open) {
			setReason("");
			setReasonError(false);
		}
	}, [open, installment.number]);

	const handleConfirm = async () => {
		if (!reason.trim()) {
			setReasonError(true);
			return;
		}

		setIsSubmitting(true);
		const result = await SinisterAgreementService.cancelInstallment(agreementId, installment.number, { reason: reason.trim() });
		setIsSubmitting(false);

		if (result instanceof Error) {
			toastHandler.present({ type: "error", messageOrContent: result.message, position: "top-right" });
			return;
		}

		toastHandler.present({
			type: "success",
			messageOrContent: `Parcela ${installment.number} cancelada.`,
			position: "top-right",
		});
		onConfirm();
	};

	return (
		<>
			<LoadingComp isopen={isSubmitting} />
			<Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3, overflow: "hidden" } }}>
				<DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
					<Typography variant="h6" color="primary" sx={{ fontWeight: 400 }}>
						Cancelar Parcela
					</Typography>
					<IconButton onClick={onClose} size="small">
						<CloseIcon fontSize="small" />
					</IconButton>
				</DialogTitle>

				<DialogContent dividers>
					<Stack spacing={2} mt={0.5}>
						<Alert severity="error" sx={{ py: 0.5 }}>
							Parcela {installment.number} — {formatScaleValueBR(String(installment.value))} — vencimento em{" "}
							{formatDateFnsRemoveTimezone(installment.dueDate) || "-"}. Essa ação cancela o boleto/cobrança dessa parcela.
						</Alert>

						<TextField
							fullWidth
							multiline
							minRows={3}
							label="Motivo do cancelamento"
							value={reason}
							onChange={(e) => {
								setReason(e.target.value);
								if (e.target.value.trim()) setReasonError(false);
							}}
							error={reasonError}
							helperText={reasonError ? "Informe o motivo do cancelamento." : ""}
							required
						/>
					</Stack>
				</DialogContent>

				<DialogActions>
					<Button onClick={onClose}>Voltar</Button>
					<Button variant="contained" color="error" disabled={isSubmitting} onClick={handleConfirm}>
						Confirmar Cancelamento
					</Button>
				</DialogActions>
			</Dialog>
		</>
	);
};
